import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

import {
  Shield, Clock, Smile, Lightbulb, Hammer, Users, Leaf, CheckCircle2,
  Mail, MessageCircle, ArrowRight
} from "lucide-react";

import { FadeUp, SectionLabel } from "../components/Motion";
import { services, HERO_IMAGE, careers } from "../data/projects";
import { backendUrl } from "../services/backendUrl";
import { buildWhatsappUrl, EMAIL } from "../lib/whatsapp";
import CareersForm from "../components/CareersForm";
import FounderSection from "../components/FounderSection";


// Construction journey imagery: Foundation → Structure → Completion
const STORY_IMAGES = {
  foundation: backendUrl("/Pearlhouse-cover.webp"),
  structure: backendUrl("/10038 (1).webp"),
  completion: backendUrl("/ClaraGarden.webp"),
};



const storyCards = [
  {
    title: "Who We Are",
    points: [
      "Built on strong civil engineering expertise",
      "35+ years of industry experience",
      "Delivered residential, commercial & industrial projects",
      "Trusted construction company in Chennai",
      "Focused on quality and long-term value"
    ],
    image: STORY_IMAGES.foundation,
    tag: "01 — Foundation",
  },
  {
    title: "What We Do",
    points: [
      "End-to-end design and construction solutions",
      "Well-planned and aesthetically pleasing spaces",
      "Strong and durable building structures",
      "Cost-effective without compromising quality",
      "Efficient project execution from start to finish"
    ],
    image: STORY_IMAGES.structure,
    tag: "02 — Structure",
  },
  {
    title: "Why Choose Us",
    points: [
      "High-quality workmanship and standards",
      "Reliable execution and project management",
      "Strict safety procedures at every stage",
      "On-time project delivery",
      "Attention to detail in every build"
    ],
    image: STORY_IMAGES.completion,
    tag: "03 — Completion",
  },
];

const values = [
  { icon: Shield, title: "Integrity" },
  { icon: Smile, title: "Customer Satisfaction" },
  { icon: Lightbulb, title: "Innovation" },
  { icon: Hammer, title: "Quality Craftsmanship" },
  { icon: Users, title: "Collaboration" },
  { icon: Leaf, title: "Sustainability" },
  { icon: CheckCircle2, title: "Accountability" },
];

function StoryCard({ card, index }) {
  const ref = useRef(null);
  const reverse = index % 2 === 1;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 80, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
      className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
      data-testid={`story-card-${index}`}
    >

      
      {/* Image side */}
      <div className="lg:col-span-7">
        <div className="relative overflow-hidden group" style={{ borderRadius: "20px" }}>
          <motion.img
            src={card.image}
            alt={card.alt || card.title}
            loading="lazy"
            style={{ scale: imgScale }}
            className="w-full h-[420px] md:h-[560px] object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
        </div>
      </div>

      {/* Content card */}
      <motion.div
        style={{ y }}
        className="lg:col-span-5 relative"
      >
        <div
          className="relative bg-[#1A1A1A]/95 backdrop-blur-xl border border-white/10 shadow-2xl p-10 md:p-12 text-white overflow-hidden"
          style={{ borderRadius: "20px" }}
        >
          {/* subtle gradient accent */}
          <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full brand-gradient opacity-30 blur-3xl" />
          <p className="relative text-xs tracking-[0.3em] uppercase text-[#E85D8E] font-medium">{card.tag}</p>
          <h3 className="relative font-serif text-4xl md:text-5xl mt-5 leading-tight tracking-tight">
            {card.title}
          </h3>
         <ul className="relative mt-7 space-y-3 text-white/80 text-base md:text-lg leading-relaxed">
  {card.points.map((item, i) => (
    <li key={i} className="flex items-start gap-3">
      <span className="mt-2 w-2 h-2 bg-[#E85D8E] rounded-full"></span>
      {item}
    </li>
  ))}
</ul>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function About({ onEnquire }) {
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(heroScroll, [0, 1], [0, 150]);
  const heroScale = useTransform(heroScroll, [0, 1], [1, 1.1]);

  return (
    <div data-testid="about-page" className="bg-[#FAFAFA]">
      {/* HERO — Our Vision */}
      <section
        ref={heroRef}
        data-testid="about-hero"
        className="relative min-h-[90vh] w-full overflow-hidden"
      >
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 will-change-transform">
          <img
            src={HERO_IMAGE}
            alt="Sanggavai vision"
            className="w-full h-full object-cover"
          />
        </motion.div>
        {/* Left → right dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/10" />

        <div className="relative z-10 min-h-[90vh] container mx-auto px-6 md:px-12 max-w-7xl flex flex-col justify-end pb-20 md:pb-28 pt-32">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
            className="text-xs md:text-sm tracking-[0.32em] text-white/70 uppercase"
          >
            About Sanggavai's
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.15 }}
            className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight mt-5 max-w-4xl"
          >
            Our <span className="italic font-light">Vision.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-8 text-white/85 text-lg md:text-2xl leading-relaxed max-w-3xl"
          >
           Sanggavai Constructions is committed to building high-quality, durable, and value-driven projects. With decades of experience, we deliver trusted construction solutions that combine design excellence with strong engineering standards.
          </motion.p>
        </div>
      </section>

    <FounderSection />

  {/* STORY / SCROLLING CARDS */}
      <section data-testid="story-section" className="py-24 md:py-36">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="text-center max-w-2xl mx-auto mb-20">
            <SectionLabel>Our Story</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 tracking-tight text-[#1A1A1A]">
              Built on trust. <br />
              <span className="italic brand-gradient-text">Driven by detail.</span>
            </h2>
          </FadeUp>

          <div className="space-y-24 md:space-y-40">
            {storyCards.map((card, i) => (
              <StoryCard key={card.title} card={card} index={i} />
            ))}
          </div>
          </div>
      </section>

      {/* VALUES — gradient section */}
      <section data-testid="values-section" className="py-24 md:py-32 brand-gradient text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-black/20 blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative">
          <FadeUp className="max-w-3xl">
            <p className="text-xs md:text-sm tracking-[0.32em] uppercase text-white/80 font-medium">Our Values</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 tracking-tight leading-[1.08]">
              The principles behind every<br className="hidden md:block" /> project we build.
            </h2>
          </FadeUp>

          <div className="mt-16 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {values.map((v, i) => (
              <FadeUp key={v.title} delay={(i % 4) * 0.06}>
                <div
                  data-testid={`value-${i}`}
                  className="group bg-white/10 backdrop-blur-md border border-white/20 p-6 md:p-8 h-full hover:-translate-y-1 hover:bg-white/15 transition-all duration-300"
                  style={{ borderRadius: "20px" }}
                >
                  <div className="h-12 w-12 rounded-full bg-white/15 border border-white/30 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#7A2E5D] transition-colors">
                    <v.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-7 font-serif text-2xl md:text-3xl leading-snug">{v.title}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES — retained minimal grid */}
      <section data-testid="services-grid" className="py-24 md:py-32 bg-[#F5F2F0]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="max-w-2xl">
            <SectionLabel>Services</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl mt-5 tracking-tight text-[#1A1A1A]">End-to-end construction.</h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {services.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.08}>
                <div
                  className="bg-white p-10 border border-black/5 h-full hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                  style={{ borderRadius: "20px" }}
                >
                  <p className="font-serif brand-gradient-text text-5xl">0{i + 1}</p>
                  <h3 className="font-serif text-3xl mt-4 text-[#1A1A1A]">{s.title}</h3>
                  <p className="mt-4 text-[#555] text-base leading-relaxed">{s.tagline}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp className="mt-16 text-center">
            <button
              type="button"
              onClick={() => onEnquire && onEnquire()}
              data-testid="about-enquire-btn"
              className="inline-flex items-center gap-2 brand-gradient text-white rounded-full px-8 py-4 font-medium hover:-translate-y-0.5 hover:shadow-xl transition-all"
            >
              Start a Project With Us <ArrowRight className="h-4 w-4" />
            </button>
          </FadeUp>
        </div>
      </section>

      <CareersForm />
    </div>
  );
}
